/**
 * Peer Valuation Radar Chart.
 *
 * Polar line chart comparing the target company's normalized valuation
 * attractiveness scores against the peer-group average.
 */

import type Highcharts from "highcharts";
import { isDarkMode, setupThemeObserver } from "../../common/chartTheme";
import { createChartLifecycle, type ChartLifecycleReflowOptions } from "./chartLifecycle";
import { escapeHtml } from "./chartUtils";
import type { PeerChartData, PeerRadarData, PeerRadarMetric } from "./peerChartTypes";
import { showChartEmptyState } from "./peerChartTypes";

interface RadarThemeColors {
  textColor: string;
  subtitleColor: string;
  gridLineColor: string;
  targetColor: string;
  peerColor: string;
  targetFill: string;
  peerFill: string;
  tooltipBg: string;
  tooltipBorder: string;
}

interface RadarPoint extends Highcharts.PointOptionsObject {
  y: number;
  custom: PeerRadarMetric;
}

const REQUIRED_RADAR_AXIS_COUNT = 3;

let themeObserver: MutationObserver | null = null;

function getThemeColors(): RadarThemeColors {
  const dark = isDarkMode();
  return {
    textColor: dark ? "#E0E0E3" : "#333333",
    subtitleColor: dark ? "#A0A0A3" : "#666666",
    gridLineColor: dark ? "#374151" : "#e5e7eb",
    targetColor: dark ? "#60a5fa" : "#0F52BA",
    peerColor: dark ? "#2dd4bf" : "#0d9488",
    targetFill: dark ? "rgba(96, 165, 250, 0.18)" : "rgba(15, 82, 186, 0.14)",
    peerFill: dark ? "rgba(45, 212, 191, 0.14)" : "rgba(13, 148, 136, 0.12)",
    tooltipBg: dark ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.98)",
    tooltipBorder: dark ? "#404043" : "#cccccc",
  };
}

function renderChartForContainer(container: HTMLElement): void {
  const configAttr = container.dataset.chartConfig;
  if (!configAttr) {
    lifecycle.destroyChart(container.id);
    return;
  }

  try {
    const scriptEl = document.getElementById(configAttr);
    if (!scriptEl) {
      lifecycle.destroyChart(container.id);
      return;
    }

    const data = JSON.parse(scriptEl.textContent || "{}") as PeerChartData;
    if (!lifecycle.isContainerVisible(container)) {
      lifecycle.destroyChart(container.id);
      return;
    }

    lifecycle.destroyChart(container.id);
    const chart = renderPeerRadarChart(container, data.radarData);
    if (chart) {
      lifecycle.instances.set(container.id, chart);
    }
  } catch (e) {
    console.warn(`Failed to render peer radar chart for ${container.id}:`, e);
    showChartEmptyState(container, getThemeColors().textColor, "Chart could not be loaded");
  }
}

const lifecycle = createChartLifecycle(".peer-radar-chart", renderChartForContainer, {
  deferUntilIntersect: true,
  rootMargin: "200px 0px",
});

function makeRadarPoints(
  metrics: PeerRadarMetric[],
  scoreKey: "targetScore" | "peerAverageScore",
): RadarPoint[] {
  return metrics.map((metric) => ({
    y: metric[scoreKey],
    custom: metric,
  }));
}

function buildTooltipRow(label: string, value: string, score: number, color: string): string {
  return (
    `<tr><td style="padding-right:10px;color:${color};">${escapeHtml(label)}</td>` +
    `<td style="text-align:right;"><b>${escapeHtml(value)}</b></td>` +
    `<td style="padding-left:10px;text-align:right;">${score.toFixed(2)}</td></tr>`
  );
}

/**
 * Render the peer valuation radar chart.
 *
 * @param container - Target DOM element.
 * @param radarData - Chart-ready radar payload.
 * @returns Highcharts chart instance or null.
 */
export function renderPeerRadarChart(
  container: HTMLElement,
  radarData: PeerRadarData | undefined,
): Highcharts.Chart | null {
  if (!container || !window.Highcharts) return null;

  const colors = getThemeColors();
  container.style.height = "420px";

  if (
    !radarData ||
    radarData.status !== "success" ||
    radarData.metrics.length < REQUIRED_RADAR_AXIS_COUNT
  ) {
    showChartEmptyState(
      container,
      colors.textColor,
      radarData?.message || "Insufficient peer data to generate valuation comparison.",
    );
    return null;
  }

  const categories = radarData.metrics.map((metric) => metric.label);
  const targetSeriesName = radarData.targetTicker
    ? `${radarData.targetName ?? "Target"} (${radarData.targetTicker})`
    : (radarData.targetName ?? "Target");

  return window.Highcharts.chart(container, {
    chart: {
      polar: true,
      type: "line",
      backgroundColor: "transparent",
      spacing: [10, 15, 10, 15],
    },
    title: {
      text: "Valuation Attractiveness",
      align: "left",
      style: { color: colors.textColor, fontSize: "14px", fontWeight: "600" },
    },
    subtitle: {
      text: radarData.snapshotDate
        ? `Snapshot ${radarData.snapshotDate} - ${radarData.peerCount} peers`
        : `${radarData.peerCount} peers`,
      align: "left",
      style: { color: colors.subtitleColor, fontSize: "11px" },
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    navigation: { buttonOptions: { enabled: false } },
    pane: {
      size: "78%",
    },
    xAxis: {
      categories,
      tickmarkPlacement: "on",
      lineWidth: 0,
      labels: {
        style: { color: colors.textColor, fontSize: "11px" },
      },
    },
    yAxis: {
      gridLineInterpolation: "polygon",
      lineWidth: 0,
      min: 0,
      max: 10,
      tickInterval: 1,
      labels: {
        style: { color: colors.subtitleColor, fontSize: "10px" },
      },
    },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.tooltipBorder,
      style: { color: colors.textColor },
      formatter() {
        const points = this.points ?? [];
        const firstPoint = points[0]?.point as Highcharts.Point & { custom?: PeerRadarMetric };
        const metric = firstPoint?.custom;
        if (!metric) return "";

        const targetPoint = points.find((point) => point.series.name === targetSeriesName);
        const peerPoint = points.find((point) => point.series.name === "Peer Average");
        const targetScore = Number(targetPoint?.y ?? metric.targetScore);
        const peerScore = Number(peerPoint?.y ?? metric.peerAverageScore);
        const note = metric.uniform
          ? `<div style="margin-top:6px;color:${colors.subtitleColor};">${escapeHtml(metric.note)}</div>`
          : "";

        return (
          '<div style="padding:6px;min-width:210px;">' +
          `<strong>${escapeHtml(metric.label)}</strong>` +
          '<table style="width:100%;margin-top:6px;font-size:11px;">' +
          '<thead><tr><th style="text-align:left;font-weight:400;"></th>' +
          '<th style="text-align:right;font-weight:400;">Raw</th>' +
          '<th style="text-align:right;font-weight:400;">Score</th></tr></thead>' +
          "<tbody>" +
          buildTooltipRow("Target", metric.targetFormatted, targetScore, colors.targetColor) +
          buildTooltipRow("Peer Avg", metric.peerAverageFormatted, peerScore, colors.peerColor) +
          "</tbody></table>" +
          `<div style="margin-top:6px;color:${colors.subtitleColor};font-size:10px;">` +
          `${metric.peerCoverage} peer values used</div>` +
          note +
          "</div>"
        );
      },
    },
    plotOptions: {
      series: {
        marker: { enabled: true, radius: 3 },
        animation: { duration: 400 },
      },
      area: {
        connectEnds: true,
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      itemStyle: { color: colors.textColor, fontSize: "11px", fontWeight: "normal" },
    },
    series: [
      {
        type: "area",
        name: targetSeriesName,
        data: makeRadarPoints(radarData.metrics, "targetScore"),
        color: colors.targetColor,
        fillColor: colors.targetFill,
        lineWidth: 2,
        pointPlacement: "on",
      },
      {
        type: "area",
        name: "Peer Average",
        data: makeRadarPoints(radarData.metrics, "peerAverageScore"),
        color: colors.peerColor,
        fillColor: colors.peerFill,
        dashStyle: "ShortDash",
        lineWidth: 2,
        pointPlacement: "on",
      },
    ],
  } as Highcharts.Options) as Highcharts.Chart;
}

/** Initialize all peer radar charts on the page. */
export function initializePeerRadarCharts(
  root: ParentNode = document,
  options: ChartLifecycleReflowOptions = {},
): void {
  const containers = lifecycle.getContainers(root);
  const activeIds = new Set<string>();

  containers.forEach((container) => {
    if (!container.id) {
      container.id = `peer-radar-${crypto.randomUUID().slice(0, 8)}`;
    }
    activeIds.add(container.id);
    lifecycle.reflowChartForContainer(container, options);
  });

  lifecycle.pruneStaleCharts(activeIds, root);

  if (!themeObserver) {
    themeObserver = setupThemeObserver(() => {
      initializePeerRadarCharts(document, { forceRender: true });
    });
  }
}

/** Register DOM listeners to auto-initialize peer radar charts. */
export function setupPeerRadarChartAutoInit(): void {
  lifecycle.setupAutoInit(initializePeerRadarCharts);
}
